import { useState, useEffect } from "react";
import { Box } from "@mui/material";

// Components do projeto
import KOGCollapse from "../kogCollapse/kogCollapse";
import KOGPaginationGrid from "../paginationGrid/paginationGrid";
import AttachmentCard from "./attachmentCard";

// Utils e API
import { quoteApi } from "@/main/api";
import { getTranslateByKey } from "@/main/services/utils";
import { PaginationEnum } from "@/main/services/enum/PaginationEnum";
import { QuoteImageResponse } from "@/main/models/response";

// Styles
import * as S from "./styles";

interface ICollapseImageCategoryProps {
	title: string;
	categoryType: "initial" | "supplementary";
	guidQuote: string;
	guidClaim?: string;
	expanded: boolean;
	onToggle: (expanded: boolean) => void;
	onPreview: (index: number, data: QuoteImageResponse[], totalRecords: number) => void;
	canDownloadImages?: boolean;
	handleDownloadImage: (url: string, imageRecord?: QuoteImageResponse) => void;
	collapseMaxHeight?: string;
	collapseOverflow?: string;
	handDeleteImage: (guid: string) => void;
	reloadTrigger?: number;
}

function CollapseImageCategory({
	title,
	categoryType,
	guidQuote,
	guidClaim,
	expanded,
	onToggle,
	onPreview,
	canDownloadImages = false,
	handleDownloadImage,
	collapseMaxHeight,
	collapseOverflow,
	handDeleteImage,
	reloadTrigger,
}: ICollapseImageCategoryProps) {
	const [dataGrid, setDataGrid] = useState<QuoteImageResponse[]>([]);
	const [totalRecords, setTotalRecords] = useState<number>(0);
	const [formFilters, setFormFilters] = useState<any | null>(null);

	const ID_SCROLLABLE_TARGET = `id-scrollable-${categoryType}`;

	useEffect(() => {
		setDataGrid([]);
		setTotalRecords(0);
		setFormFilters({
			guidClaim: guidClaim ?? null,
			guidQuote: guidQuote,
			pageIndex: PaginationEnum.PageIndexIntial,
			pageSize: PaginationEnum.PageSize10,
			includeInitialQuoteImages: categoryType === "initial",
			includeComplementQuoteImages: categoryType === "supplementary",
			_reloadTrigger: reloadTrigger,
		});
	}, [guidQuote, guidClaim, categoryType, reloadTrigger]);

	useEffect(() => {
		if (!expanded) {
			setDataGrid([]);
			setTotalRecords(0);
		}
	}, [expanded]);

	function fetchData(request: any) {
		return quoteApi.getAllClaimImages(request).then((response) => response.data);
	}

	return (
		<KOGCollapse
			header={<span style={{ fontWeight: "bold" }}>{title}</span>}
			isOpenCollapseinitial={expanded}
			forceCollapseTrigger={!expanded}
			onCustomCollapse={onToggle}
			maxHeight={collapseMaxHeight}
			overflow={collapseOverflow}
			isFlexAccordion
			content={
				<Box
					px={1}
					py={1.5}
					sx={{
						display: "flex",
						flexDirection: "column",
						flex: 1,
						minHeight: 0,
					}}
				>
					<S.PaginationLabel>
						{totalRecords > 0 &&
							getTranslateByKey("information.photoPaginationCount")
								.replace("{0}", dataGrid.length.toString())
								.replace("{1}", totalRecords.toString())}
					</S.PaginationLabel>
					<S.ContainerScoll id={ID_SCROLLABLE_TARGET} style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
						<KOGPaginationGrid
							idScrollableTarget={ID_SCROLLABLE_TARGET}
							showTitleResults={false}
							showEndMessage={false}
							totalRecords={totalRecords}
							setTotalRecords={setTotalRecords}
							dataGrid={dataGrid}
							formFilters={formFilters}
							setDataGrid={setDataGrid}
							fetchMore={fetchData}
						>
							{dataGrid.length > 0 && (
								<S.ContainerPreview previewImage={"column"}>
									{dataGrid.map((quoteImage, index) => (
										<AttachmentCard
											key={`${quoteImage.guid}-${index}`}
											ids={{
												card: `id-${categoryType}-itemPreview`,
												image: `id-${categoryType}-img-preview-${quoteImage.fileName}`,
												deleteButton: `id-dragDrop-btn-removePreview-${index}`,
												downloadButton: `id-${categoryType}-btn-download-${index}`,
												comment: `id-${categoryType}-comment-${index}`,
											}}
											fileName={quoteImage.fileName}
											comment={quoteImage.comment}
											isImage
											imageSource={quoteImage.externalKey}
											canDelete={quoteImage.canDelete}
											canDownload={canDownloadImages}
											onDelete={() => handDeleteImage(quoteImage.guid)}
											onDownload={() => handleDownloadImage(quoteImage.externalKey, quoteImage)}
											onPreview={() => onPreview(index, dataGrid, totalRecords)}
										/>
									))}
								</S.ContainerPreview>
							)}
						</KOGPaginationGrid>
					</S.ContainerScoll>
				</Box>
			}
		/>
	);
}

export default CollapseImageCategory;
